import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  DollarSign,
  Plus,
  Search,
  Trash2,
  Loader2,
  AlertTriangle,
  CreditCard,
  Banknote,
  Wallet
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const RentPayments = () => {
  const { token, isManager } = useAuth();
  const [payments, setPayments] = useState([]);
  const [leases, setLeases] = useState([]);
  const [lateRents, setLateRents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletePayment, setDeletePayment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("payments");
  const [sendingReminder, setSendingReminder] = useState(null);

  const [formData, setFormData] = useState({
    lease_id: "",
    tenant_id: "",
    property_id: "",
    amount: "",
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: "zelle",
    period_month: new Date().toISOString().slice(0, 7),
    notes: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [paymentsRes, leasesRes, lateRes] = await Promise.all([
        axios.get(`${API}/rent-payments`, { headers }),
        axios.get(`${API}/leases`, { headers }),
        axios.get(`${API}/rent-payments/late`, { headers })
      ]);
      setPayments(paymentsRes.data);
      setLeases(leasesRes.data.filter(l => l.status === "active"));
      setLateRents(lateRes.data);
    } catch (error) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const resetForm = () => {
    setFormData({
      lease_id: "",
      tenant_id: "",
      property_id: "",
      amount: "",
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: "zelle",
      period_month: new Date().toISOString().slice(0, 7),
      notes: ""
    });
  };

  const handleLeaseSelect = (leaseId) => {
    const lease = leases.find(l => l.id === leaseId);
    if (lease) {
      setFormData({
        ...formData,
        lease_id: leaseId,
        tenant_id: lease.tenant_id,
        property_id: lease.property_id,
        amount: lease.monthly_rent.toString()
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.lease_id || !formData.amount || !formData.payment_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount)
    };

    setSaving(true);
    try {
      await axios.post(`${API}/rent-payments`, submitData, { headers });
      toast.success("Payment recorded successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/rent-payments/${deletePayment.id}`, { headers });
      toast.success("Payment deleted successfully");
      setDeletePayment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete payment");
    }
  };

  const handleSendReminder = async (tenantId) => {
    setSendingReminder(tenantId);
    try {
      await axios.post(`${API}/send-rent-reminder/${tenantId}`, {}, { headers });
      toast.success("Reminder sent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case "zelle": return <Wallet className="w-4 h-4" />;
      case "check": return <CreditCard className="w-4 h-4" />;
      case "cash": return <Banknote className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  const filteredPayments = payments.filter(payment =>
    payment.tenant?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payment.property?.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <div className="skeleton h-8 w-48" />
        </div>
        <div className="skeleton h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="rent-payments-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Rent Payments</h1>
          <p className="text-slate-500 mt-1">{payments.length} payment records</p>
        </div>
        {isManager && (
          <Button onClick={() => setDialogOpen(true)} className="bg-green-700 hover:bg-green-800" data-testid="add-payment-button">
            <Plus className="w-4 h-4 mr-2" />
            Record Payment
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="payments" data-testid="tab-payments">All Payments</TabsTrigger>
          <TabsTrigger value="late" data-testid="tab-late" className="relative">
            Late Rents
            {lateRents.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                {lateRents.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payments">
          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search payments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="payment-search-input"
              />
            </div>
          </div>

          {filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 mb-4">No payments recorded yet</p>
              {isManager && (
                <Button onClick={() => setDialogOpen(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Record first payment
                </Button>
              )}
            </div>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Date</TableHead>
                    {isManager && <TableHead className="w-16">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.map((payment) => (
                    <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                      <TableCell className="font-medium">{payment.tenant?.name || "N/A"}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {payment.property?.address || "N/A"}
                      </TableCell>
                      <TableCell>{payment.period_month}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getPaymentMethodIcon(payment.payment_method)}
                          <span className="capitalize">{payment.payment_method}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums text-green-700">
                        ${payment.amount?.toLocaleString()}
                      </TableCell>
                      <TableCell>{payment.payment_date}</TableCell>
                      {isManager && (
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-600" 
                            onClick={() => setDeletePayment(payment)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="late">
          {lateRents.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DollarSign className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">All rents are paid!</h3>
                  <p className="text-slate-500">No late payments at this time.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {lateRents.map((item, index) => (
                <Card key={index} className="border-l-4 border-l-red-500">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                          <AlertTriangle className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{item.tenant?.name}</h3>
                          <p className="text-sm text-slate-500">{item.property?.address}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className="bg-red-100 text-red-700">
                              {item.days_late} days late
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Amount Due</p>
                          <p className="text-xl font-bold text-red-600 tabular-nums">
                            ${item.amount_due?.toLocaleString()}
                          </p>
                        </div>
                        {isManager && item.tenant?.email && (
                          <Button
                            variant="outline"
                            onClick={() => handleSendReminder(item.tenant.id)}
                            disabled={sendingReminder === item.tenant.id}
                            data-testid={`send-reminder-${item.tenant.id}`}
                          >
                            {sendingReminder === item.tenant.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              "Send Reminder"
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Rent Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Lease *</Label>
              <Select
                value={formData.lease_id}
                onValueChange={handleLeaseSelect}
              >
                <SelectTrigger data-testid="payment-lease-select">
                  <SelectValue placeholder="Select lease" />
                </SelectTrigger>
                <SelectContent>
                  {leases.map((lease) => (
                    <SelectItem key={lease.id} value={lease.id}>
                      {lease.tenant?.name} - {lease.property?.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="payment-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Period (Month) *</Label>
                <Input
                  type="month"
                  value={formData.period_month}
                  onChange={(e) => setFormData({ ...formData, period_month: e.target.value })}
                  data-testid="payment-period-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  data-testid="payment-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Method *</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" disabled={saving} data-testid="save-payment-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Record Payment"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletePayment} onOpenChange={() => setDeletePayment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this payment record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RentPayments;
